import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { RichEmailEditor } from "@/components/RichEmailEditor";

const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled"];

function StudentsTab() {
  const [students, setStudents] = useState([]);
  useEffect(() => {
    api.get("/admin/students").then((r) => setStudents(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  return (
    <div className="border border-[#2E2E2A]" data-testid="students-table">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2E2E2A] hover:bg-transparent">
            <TableHead className="text-[#A3A39C]">Name</TableHead>
            <TableHead className="text-[#A3A39C]">Email</TableHead>
            <TableHead className="text-[#A3A39C]">Package</TableHead>
            <TableHead className="text-[#A3A39C]">Role</TableHead>
            <TableHead className="text-[#A3A39C]">Community</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map((s) => (
            <TableRow key={s.id} className="border-[#2E2E2A] hover:bg-[#252522]" data-testid={`student-row-${s.id}`}>
              <TableCell>{s.name}</TableCell>
              <TableCell className="text-[#A3A39C]">{s.email}</TableCell>
              <TableCell>
                {s.packages && s.packages.length ? (
                  <span className="border border-[#C49A6C] px-2 py-0.5 text-xs uppercase tracking-[0.1em] text-[#C49A6C]">{s.packages.join(", ")}</span>
                ) : (
                  <span className="text-[#A3A39C]">—</span>
                )}
              </TableCell>
              <TableCell className="text-[#A3A39C]">{s.role}</TableCell>
              <TableCell>
                {s.community_access ? (
                  <span data-testid={`community-member-${s.id}`} className="border border-[#C49A6C] px-2 py-0.5 text-xs uppercase tracking-[0.1em] text-[#C49A6C]">member</span>
                ) : (
                  <span className="text-[#A3A39C]">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-[#2E2E2A]" data-testid="admin-bookings-table">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2E2E2A] hover:bg-transparent">
            <TableHead className="text-[#A3A39C]">Student</TableHead>
            <TableHead className="text-[#A3A39C]">Type</TableHead>
            <TableHead className="text-[#A3A39C]">Date & Time</TableHead>
            <TableHead className="text-[#A3A39C]">Notes</TableHead>
            <TableHead className="text-[#A3A39C]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.length === 0 && (
            <TableRow className="border-[#2E2E2A] hover:bg-transparent">
              <TableCell colSpan={5} className="py-8 text-center text-[#A3A39C]" data-testid="admin-no-bookings">No bookings yet.</TableCell>
            </TableRow>
          )}
          {bookings.map((b) => (
            <TableRow key={b.id} className="border-[#2E2E2A] hover:bg-[#252522]" data-testid={`admin-booking-row-${b.id}`}>
              <TableCell>
                <p>{b.user_name}</p>
                <p className="text-xs text-[#A3A39C]">{b.user_email}</p>
              </TableCell>
              <TableCell>{b.type === "one_on_one" ? "1-on-1" : b.type === "field_training" ? "Field Training" : b.type === "paid_call" ? "Paid Call" : "Consultation"}</TableCell>
              <TableCell className="text-[#A3A39C]">{b.date} {b.time}</TableCell>
              <TableCell className="max-w-[200px] truncate text-[#A3A39C]">{b.notes || "—"}</TableCell>
              <TableCell>
                <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                  <SelectTrigger data-testid={`admin-booking-status-${b.id}`} className="w-[130px] rounded-none border-[#2E2E2A] bg-[#141413]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]">
                    {BOOKING_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ModulesTab() {
  const [modules, setModules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", slides_url: "", duration: "" });
  const load = () => api.get("/admin/modules").then((r) => setModules(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openEdit = (m) => {
    setEditing(m);
    setForm({ title: m.title, description: m.description, video_url: m.video_url, slides_url: m.slides_url, duration: m.duration });
    setVideoFile(null);
    setWorkbookFile(null);
  };

  const [workbookFile, setWorkbookFile] = useState(null);
  const [uploadingWorkbook, setUploadingWorkbook] = useState(false);

  const uploadWorkbook = async () => {
    if (!workbookFile || !editing) return;
    setUploadingWorkbook(true);
    const fd = new FormData();
    fd.append("module_id", editing.id);
    fd.append("file", workbookFile);
    try {
      await api.post("/admin/workbooks", fd);
      toast.success("Workbook PDF uploaded.");
      setWorkbookFile(null);
      load();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingWorkbook(false);
    }
  };

  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadVideo = async () => {
    if (!videoFile || !editing) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("module_id", editing.id);
    fd.append("file", videoFile);
    try {
      await api.post("/admin/videos", fd);
      toast.success("Protected video uploaded.");
      setVideoFile(null);
      load();
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      await api.put(`/admin/modules/${editing.id}`, form);
      toast.success("Module updated.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <div className="divide-y divide-[#2E2E2A] border border-[#2E2E2A]" data-testid="admin-modules-list">
        {modules.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-4 bg-[#1E1E1C] px-6 py-4" data-testid={`admin-module-row-${m.order}`}>
            <span className="font-heading text-xl text-[#C49A6C]">{String(m.order).padStart(2, "0")}</span>
            <div className="flex-1">
              <p className="font-heading text-lg">{m.title}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-[#A3A39C]">
                {m.tier} · {m.video_file ? "hosted video (protected)" : m.video_url ? "video set" : "no video"} · {m.workbook_file ? "workbook (protected)" : m.slides_url ? "workbook link" : "no workbook"}
              </p>
            </div>
            <button
              data-testid={`admin-module-edit-${m.order}`}
              onClick={() => openEdit(m)}
              className="border border-[#2E2E2A] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:border-[#C49A6C] hover:text-[#C49A6C]"
            >
              Edit
            </button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="rounded-none border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]" data-testid="module-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">Edit Module {editing?.order}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {[
              { key: "title", label: "Title", testid: "edit-module-title" },
              { key: "description", label: "Description", testid: "edit-module-description" },
              { key: "video_url", label: "Video URL (YouTube only — must be Unlisted or Public)", testid: "edit-module-video" },
              { key: "slides_url", label: "Workbook link (optional — Google Drive etc.)", testid: "edit-module-slides" },
              { key: "duration", label: "Duration (e.g. 24 min)", testid: "edit-module-duration" },
            ].map((f) => (
              <div key={f.key} className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A39C]">{f.label}</Label>
                <Input
                  data-testid={f.testid}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="rounded-none border-[#2E2E2A] bg-[#141413] text-[#F4F4F0]"
                />
              </div>
            ))}
            <div className="space-y-2 border-t border-[#2E2E2A] pt-4">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A39C]">Protected video upload (mp4 / mov / webm)</Label>
              {editing?.video_file ? (
                <p className="text-xs text-[#C49A6C]">Hosted video active — playback links expire after 2 hours and can't be shared outside the academy.</p>
              ) : (
                <p className="text-xs text-[#A3A39C]">Uploaded videos are hosted privately — shared links stop working after 2 hours.</p>
              )}
              {editing?.video_file && (
                <button
                  data-testid="module-video-remove"
                  onClick={async () => {
                    try {
                      await api.delete(`/admin/modules/${editing.id}/video`);
                      toast.success("Hosted video removed.");
                      load();
                      setEditing(null);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="text-xs uppercase tracking-[0.15em] text-red-400/80 transition-colors duration-200 hover:text-red-400"
                >
                  Remove hosted video
                </button>
              )}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,.m4v"
                data-testid="module-video-file-input"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[#A3A39C] file:mr-4 file:border file:border-[#2E2E2A] file:bg-[#141413] file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.15em] file:text-[#F4F4F0]"
              />
              {videoFile && (
                <Button
                  data-testid="module-video-upload-button"
                  onClick={uploadVideo}
                  disabled={uploading}
                  className="w-full rounded-none border border-[#C49A6C] bg-transparent py-4 text-xs uppercase tracking-[0.15em] text-[#C49A6C] hover:bg-[#C49A6C] hover:text-[#141413]"
                >
                  {uploading ? "Uploading... (large files take a while)" : "Upload Protected Video"}
                </Button>
              )}
            </div>
            <div className="space-y-2 border-t border-[#2E2E2A] pt-4">
              <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A39C]">Workbook PDF upload (printable)</Label>
              {editing?.workbook_file ? (
                <p className="text-xs text-[#C49A6C]">Workbook hosted — students print it from their dashboard via a secure expiring link.</p>
              ) : (
                <p className="text-xs text-[#A3A39C]">Upload the printable workbook PDF for this module.</p>
              )}
              {editing?.workbook_file && (
                <button
                  data-testid="module-workbook-remove"
                  onClick={async () => {
                    try {
                      await api.delete(`/admin/modules/${editing.id}/workbook`);
                      toast.success("Workbook removed.");
                      load();
                      setEditing(null);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="text-xs uppercase tracking-[0.15em] text-red-400/80 transition-colors duration-200 hover:text-red-400"
                >
                  Remove workbook
                </button>
              )}
              <input
                type="file"
                accept="application/pdf"
                data-testid="module-workbook-file-input"
                onChange={(e) => setWorkbookFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[#A3A39C] file:mr-4 file:border file:border-[#2E2E2A] file:bg-[#141413] file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.15em] file:text-[#F4F4F0]"
              />
              {workbookFile && (
                <Button
                  data-testid="module-workbook-upload-button"
                  onClick={uploadWorkbook}
                  disabled={uploadingWorkbook}
                  className="w-full rounded-none border border-[#C49A6C] bg-transparent py-4 text-xs uppercase tracking-[0.15em] text-[#C49A6C] hover:bg-[#C49A6C] hover:text-[#141413]"
                >
                  {uploadingWorkbook ? "Uploading..." : "Upload Workbook PDF"}
                </Button>
              )}
            </div>
            <Button
              data-testid="module-save-button"
              onClick={save}
              className="w-full rounded-none bg-[#C49A6C] py-5 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]"
            >
              Save Module
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AvailabilityTab() {
  const [blocked, setBlocked] = useState([]);
  const [date, setDate] = useState(null);
  const load = () => api.get("/bookings/blocked-dates").then((r) => setBlocked(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const block = async () => {
    if (!date) return;
    try {
      await api.post("/admin/blocked-dates", { date: format(date, "yyyy-MM-dd") });
      toast.success("Date blocked — students can't book it.");
      setDate(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const unblock = async (d) => {
    try {
      await api.delete(`/admin/blocked-dates/${d}`);
      toast.success("Date unblocked.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid grid-cols-1 gap-px border border-[#2E2E2A] bg-[#2E2E2A] lg:grid-cols-2" data-testid="availability-tab">
      <div className="bg-[#1E1E1C] p-8">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Pick a date to block off</p>
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          disabled={{ before: tomorrow }}
          data-testid="block-calendar"
          className="border border-[#2E2E2A] bg-[#141413] text-[#F4F4F0]"
        />
        <Button
          data-testid="block-date-button"
          onClick={block}
          disabled={!date}
          className="mt-6 rounded-none bg-[#C49A6C] px-6 py-4 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]"
        >
          Block This Date
        </Button>
      </div>
      <div className="bg-[#1E1E1C] p-8">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Blocked dates</p>
        {blocked.length === 0 ? (
          <p className="text-sm text-[#A3A39C]" data-testid="no-blocked-dates">No dates blocked — students can book any open slot.</p>
        ) : (
          <div className="divide-y divide-[#2E2E2A] border border-[#2E2E2A]">
            {[...blocked].sort().map((d) => (
              <div key={d} className="flex items-center justify-between px-4 py-3" data-testid={`blocked-date-${d}`}>
                <span className="text-sm">{d}</span>
                <button onClick={() => unblock(d)} data-testid={`unblock-${d}`} className="text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-red-400">
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="mt-6 text-xs leading-relaxed text-[#A3A39C]">
          Blocked dates can't be booked by students — use this for personal days, vacations, or fully-booked days. Sessions already booked on a date you're blocking stay on the books; cancel them from the Bookings tab if needed.
        </p>
      </div>
    </div>
  );
}

function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const load = () => api.get("/admin/service-requests").then((r) => setRequests(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/service-requests/${id}`, { status });
      toast.success(`Request marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-[#2E2E2A]" data-testid="admin-requests-table">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2E2E2A] hover:bg-transparent">
            <TableHead className="text-[#A3A39C]">Service</TableHead>
            <TableHead className="text-[#A3A39C]">Customer</TableHead>
            <TableHead className="text-[#A3A39C]">Note</TableHead>
            <TableHead className="text-[#A3A39C]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.length === 0 && (
            <TableRow className="border-[#2E2E2A] hover:bg-transparent">
              <TableCell colSpan={4} className="py-8 text-center text-[#A3A39C]" data-testid="admin-no-requests">No service requests yet.</TableCell>
            </TableRow>
          )}
          {requests.map((r) => (
            <TableRow key={r.id} className="border-[#2E2E2A] hover:bg-[#252522]" data-testid={`admin-request-row-${r.id}`}>
              <TableCell>{r.service}</TableCell>
              <TableCell>
                <p>{r.name}</p>
                <p className="text-xs text-[#A3A39C]">{r.email}</p>
              </TableCell>
              <TableCell className="max-w-[240px] truncate text-[#A3A39C]" title={r.note}>{r.note || "—"}</TableCell>
              <TableCell>
                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger data-testid={`admin-request-status-${r.id}`} className="w-[130px] rounded-none border-[#2E2E2A] bg-[#141413]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]">
                    {["new", "contacted", "closed"].map((s) => (
                      <SelectItem key={s} value={s} data-testid={`request-status-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DfyTab() {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/admin/dfy").then((r) => setApps(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try {
      await api.patch(`/admin/dfy/${id}`, { status });
      toast.success(`Application ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-[#2E2E2A]" data-testid="admin-dfy-table">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2E2E2A] hover:bg-transparent">
            <TableHead className="text-[#A3A39C]">Applicant</TableHead>
            <TableHead className="text-[#A3A39C]">Experience</TableHead>
            <TableHead className="text-[#A3A39C]">Budget</TableHead>
            <TableHead className="text-[#A3A39C]">Area / Travel</TableHead>
            <TableHead className="text-[#A3A39C]">Status</TableHead>
            <TableHead className="text-[#A3A39C]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {apps.length === 0 && (
            <TableRow className="border-[#2E2E2A] hover:bg-transparent">
              <TableCell colSpan={6} className="py-8 text-center text-[#A3A39C]" data-testid="admin-no-dfy">No applications yet.</TableCell>
            </TableRow>
          )}
          {apps.map((a) => (
            <TableRow key={a.id} className="border-[#2E2E2A] hover:bg-[#252522]" data-testid={`admin-dfy-row-${a.id}`}>
              <TableCell>
                <p>{a.name}</p>
                <p className="text-xs text-[#A3A39C]">{a.email}</p>
                <p className="text-xs text-[#A3A39C]">{a.phone}</p>
                {a.social && <p className="text-xs text-[#A3A39C]">{a.social}</p>}
                {a.comments && <p className="mt-1 text-xs italic text-[#A3A39C]">"{a.comments}"</p>}
              </TableCell>
              <TableCell className="text-[#A3A39C]">{a.experience}</TableCell>
              <TableCell>{a.budget}</TableCell>
              <TableCell>{a.area}<br /><span className="text-xs text-[#A3A39C]">{a.distance}</span></TableCell>
              <TableCell>
                <span data-testid={`dfy-status-${a.id}`} className={`border px-2 py-0.5 text-xs uppercase tracking-[0.1em] ${a.status === "approved" || a.status === "paid" ? "border-[#C49A6C] text-[#C49A6C]" : a.status === "declined" ? "border-red-900/60 text-red-400" : "border-[#2E2E2A] text-[#A3A39C]"}`}>
                  {a.status}
                </span>
              </TableCell>
              <TableCell>
                {a.status === "pending" && (
                  <div className="flex gap-2">
                    <button data-testid={`dfy-approve-${a.id}`} onClick={() => decide(a.id, "approved")} className="border border-[#C49A6C] px-3 py-1.5 text-xs uppercase tracking-[0.1em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]">
                      Approve
                    </button>
                    <button data-testid={`dfy-decline-${a.id}`} onClick={() => decide(a.id, "declined")} className="border border-[#2E2E2A] px-3 py-1.5 text-xs uppercase tracking-[0.1em] text-[#A3A39C] transition-colors duration-200 hover:text-red-400">
                      Decline
                    </button>
                  </div>
                )}
                {a.status === "approved" && <span className="text-xs text-[#A3A39C]">Awaiting payment</span>}
                {a.status === "paid" && <span className="text-xs text-[#C49A6C]">Paid — schedule review call</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function NewsletterTab() {
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState("");
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const load = () => {
    api.get("/admin/newsletter").then((r) => {
      setData(r.data);
      setSelected(new Set([...r.data.members.map((m) => m.email), ...r.data.subscribers.map((s) => s.email)]));
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/newsletter/history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const importEmails = async () => {
    if (!emails.trim()) return;
    setImporting(true);
    try {
      const r = await api.post("/admin/newsletter/import", { emails });
      toast.success(`Imported ${r.data.added} subscriber(s).`);
      setEmails("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const remove = async (email) => {
    try {
      await api.delete(`/admin/newsletter/${encodeURIComponent(email)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const members = data?.members || [];
  const subs = data?.subscribers || [];

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [editorInit, setEditorInit] = useState("");
  const [history, setHistory] = useState([]);
  const [viewCampaign, setViewCampaign] = useState(null);

  const toggleSelect = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const reuse = (c) => {
    setSubject(c.subject);
    setBody(c.body);
    setEditorInit(c.body);
    setEditorKey((k) => k + 1);
    setViewCampaign(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Loaded into the composer — edit and send when ready.");
  };

  const sendNewsletter = async () => {
    if (!subject.trim() || !body.trim() || selected.size === 0) return;
    if (!window.confirm(`Send this newsletter to ${selected.size} selected recipient(s)?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send", { subject, body_html: body, recipients: [...selected] });
      toast.success(`Newsletter is being sent to ${r.data.recipients} recipient(s).`);
      setSubject("");
      setBody("");
      setEditorInit("");
      setEditorKey((k) => k + 1);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-px border border-[#2E2E2A] bg-[#2E2E2A] lg:grid-cols-2" data-testid="newsletter-tab">
      <div className="bg-[#1E1E1C] p-8 lg:col-span-2">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Compose newsletter</p>
        <Input
          data-testid="newsletter-subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line"
          className="rounded-none border-[#2E2E2A] bg-[#141413] text-[#F4F4F0] placeholder:text-[#A3A39C]/60"
        />
        <div className="mt-4">
          <RichEmailEditor key={editorKey} initialHtml={editorInit} onChange={setBody} testid="newsletter-body-input" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Button
            data-testid="newsletter-send-button"
            onClick={sendNewsletter}
            disabled={sending || !subject.trim() || !body.trim() || selected.size === 0}
            className="rounded-none bg-[#C49A6C] px-6 py-4 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]"
          >
            {sending ? "Sending..." : "Send Newsletter"}
          </Button>
          <p className="text-xs text-[#A3A39C]" data-testid="newsletter-recipient-count">
            {selected.size === members.length + subs.length
              ? `Goes to your whole list — ${selected.size} recipient(s)`
              : `Goes to ${selected.size} of ${members.length + subs.length} selected recipient(s)`}
          </p>
        </div>
      </div>
      <div className="bg-[#1E1E1C] p-8">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Import previous subscribers</p>
        <Textarea
          data-testid="newsletter-import-input"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder={"Paste emails — one per line or comma-separated\njane@example.com\njohn@example.com"}
          className="min-h-[180px] rounded-none border-[#2E2E2A] bg-[#141413] text-[#F4F4F0] placeholder:text-[#A3A39C]/60"
        />
        <Button
          data-testid="newsletter-import-button"
          onClick={importEmails}
          disabled={importing || !emails.trim()}
          className="mt-4 rounded-none bg-[#C49A6C] px-6 py-4 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]"
        >
          {importing ? "Importing..." : "Import Subscribers"}
        </Button>
      </div>
      <div className="bg-[#1E1E1C] p-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#A3A39C]">
            Your newsletter list ({selected.size}/{members.length + subs.length} selected)
          </p>
          <div className="flex gap-3">
            <button data-testid="newsletter-select-all" onClick={() => setSelected(new Set([...members.map((m) => m.email), ...subs.map((s) => s.email)]))} className="text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:text-[#F4F4F0]">Select all</button>
            <button data-testid="newsletter-select-none" onClick={() => setSelected(new Set())} className="text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-[#F4F4F0]">Clear</button>
          </div>
        </div>
        <div className="max-h-[420px] divide-y divide-[#2E2E2A] overflow-y-auto border border-[#2E2E2A]">
          {members.map((m) => (
            <div key={m.email} className="flex items-center justify-between px-4 py-3" data-testid={`newsletter-member-${m.email}`}>
              <div className="flex flex-1 items-center">
                <input type="checkbox" checked={selected.has(m.email)} onChange={() => toggleSelect(m.email)} data-testid={`newsletter-select-${m.email}`} className="mr-3 h-4 w-4 shrink-0 accent-[#C49A6C]" />
                <div>
                <p className="text-sm">{m.name}</p>
                <p className="text-xs text-[#A3A39C]">{m.email}</p>
                </div>
              </div>
              <span className="border border-[#C49A6C] px-2 py-0.5 text-xs uppercase tracking-[0.1em] text-[#C49A6C]">member</span>
            </div>
          ))}
          {subs.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-3" data-testid={`newsletter-sub-${s.id}`}>
              <div className="flex items-center">
                <input type="checkbox" checked={selected.has(s.email)} onChange={() => toggleSelect(s.email)} data-testid={`newsletter-select-${s.email}`} className="mr-3 h-4 w-4 shrink-0 accent-[#C49A6C]" />
                <p className="text-sm text-[#A3A39C]">{s.email}</p>
              </div>
              <button onClick={() => remove(s.email)} data-testid={`newsletter-remove-${s.id}`} className="text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-red-400">
                Remove
              </button>
            </div>
          ))}
          {members.length + subs.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-[#A3A39C]" data-testid="newsletter-empty">No subscribers yet — import your previous list or grow it with Community members.</p>
          )}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-[#A3A39C]">
          Members join automatically when they buy Community &amp; Networking (free with any package). Use Compose Newsletter above to email your whole list — no external tool needed.
        </p>
      </div>
      <div className="bg-[#1E1E1C] p-8 lg:col-span-2">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Past newsletters</p>
        {history.length === 0 ? (
          <p className="text-sm text-[#A3A39C]" data-testid="newsletter-history-empty">Nothing sent yet — your sent newsletters will appear here.</p>
        ) : (
          <div className="divide-y divide-[#2E2E2A] border border-[#2E2E2A]" data-testid="newsletter-history">
            {history.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`newsletter-history-${c.id}`}>
                <div>
                  <p className="text-sm text-[#F4F4F0]">{c.subject}</p>
                  <p className="text-xs text-[#A3A39C]">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ""} — {c.completed ? `${c.sent} delivered${c.failed ? `, ${c.failed} failed` : ""}` : "sending..."} of {c.recipients} recipient(s)
                  </p>
                </div>
                <div className="flex gap-4">
                  <button onClick={() => setViewCampaign(c)} data-testid={`newsletter-view-${c.id}`} className="text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:text-[#F4F4F0]">View</button>
                  <button onClick={() => reuse(c)} data-testid={`newsletter-reuse-${c.id}`} className="text-xs uppercase tracking-[0.15em] text-[#A3A39C] transition-colors duration-200 hover:text-[#F4F4F0]">Reuse</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!viewCampaign} onOpenChange={() => setViewCampaign(null)}>
        <DialogContent className="max-w-2xl border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]" data-testid="newsletter-view-modal">
          <DialogHeader><DialogTitle>{viewCampaign?.subject}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto bg-white p-6 text-[#1C1C19]" dangerouslySetInnerHTML={{ __html: viewCampaign?.body || "" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success(`Quote marked ${status}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-[#2E2E2A]" data-testid="admin-quotes-table">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2E2E2A] hover:bg-transparent">
            <TableHead className="text-[#A3A39C]">Customer</TableHead>
            <TableHead className="text-[#A3A39C]">Project</TableHead>
            <TableHead className="text-[#A3A39C]">Details</TableHead>
            <TableHead className="text-[#A3A39C]">Budget / Timeline</TableHead>
            <TableHead className="text-[#A3A39C]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {quotes.length === 0 && (
            <TableRow className="border-[#2E2E2A] hover:bg-transparent">
              <TableCell colSpan={5} className="py-8 text-center text-[#A3A39C]" data-testid="admin-no-quotes">No quote requests yet.</TableCell>
            </TableRow>
          )}
          {quotes.map((q) => (
            <TableRow key={q.id} className="border-[#2E2E2A] hover:bg-[#252522]" data-testid={`admin-quote-row-${q.id}`}>
              <TableCell>
                <p>{q.name}</p>
                <p className="text-xs text-[#A3A39C]">{q.email}</p>
                <p className="text-xs text-[#A3A39C]">{q.phone}</p>
              </TableCell>
              <TableCell>
                <p>{q.project_type}</p>
                <p className="text-xs text-[#A3A39C]">{q.home_type}</p>
              </TableCell>
              <TableCell className="max-w-[220px] truncate text-[#A3A39C]" title={q.description}>{q.description}</TableCell>
              <TableCell className="text-[#A3A39C]">{q.budget || "—"}<br /><span className="text-xs">{q.timeline}</span></TableCell>
              <TableCell>
                <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                  <SelectTrigger data-testid={`admin-quote-status-${q.id}`} className="w-[130px] rounded-none border-[#2E2E2A] bg-[#141413]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#2E2E2A] bg-[#1E1E1C] text-[#F4F4F0]">
                    {["new", "contacted", "quoted", "closed"].map((s) => (
                      <SelectItem key={s} value={s} data-testid={`quote-status-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CommunityLinkEditor() {
  const [link, setLink] = useState("");
  useEffect(() => {
    api.get("/community/link").then((r) => setLink(r.data.link)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      await api.put("/admin/community-link", { link });
      toast.success("Community link saved — members see it on their dashboard.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="mb-8 mt-6 flex flex-wrap items-end gap-4 border border-[#2E2E2A] bg-[#1E1E1C] p-6" data-testid="community-link-editor">
      <div className="min-w-[260px] flex-1 space-y-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A39C]">Community invite link — shown to members (newsletter list = all members)</Label>
        <Input data-testid="community-link-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste the community link when it's ready" className="rounded-none border-[#2E2E2A] bg-[#141413] text-[#F4F4F0]" />
      </div>
      <Button data-testid="community-link-save" onClick={save} className="rounded-none bg-[#C49A6C] px-6 py-4 text-xs uppercase tracking-[0.15em] text-[#141413] hover:bg-[#F4F4F0]">
        Save Link
      </Button>
    </div>
  );
}

export default function Admin() {
  return (
    <AppShell>
      <div data-testid="admin-page">
        <p className="text-xs uppercase tracking-[0.2em] text-[#A3A39C]">Command Center</p>
        <h1 data-testid="admin-heading" className="mt-3 font-heading text-4xl font-light sm:text-5xl">Admin.</h1>
        <CommunityLinkEditor />
        <Tabs defaultValue="students" className="mt-6">
          <TabsList className="rounded-none border border-[#2E2E2A] bg-[#1E1E1C]">
            <TabsTrigger value="students" data-testid="tab-students" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Students</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Bookings</TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Modules</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Quotes</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Requests</TabsTrigger>
            <TabsTrigger value="dfy" data-testid="tab-dfy" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">DFY</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Newsletter</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-none data-[state=active]:bg-[#C49A6C] data-[state=active]:text-[#141413]">Availability</TabsTrigger>
          </TabsList>
          <TabsContent value="students" className="mt-6"><StudentsTab /></TabsContent>
          <TabsContent value="bookings" className="mt-6"><BookingsTab /></TabsContent>
          <TabsContent value="modules" className="mt-6"><ModulesTab /></TabsContent>
          <TabsContent value="quotes" className="mt-6"><QuotesTab /></TabsContent>
          <TabsContent value="requests" className="mt-6"><RequestsTab /></TabsContent>
          <TabsContent value="dfy" className="mt-6"><DfyTab /></TabsContent>
          <TabsContent value="newsletter" className="mt-6"><NewsletterTab /></TabsContent>
          <TabsContent value="availability" className="mt-6"><AvailabilityTab /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
